import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { storageLocationsApi } from '@/api/storageLocationsApi';
import { suppliesApi } from '@/api/suppliesApi';
import { DetailPageLayout } from '@/components/detail/DetailPageLayout';
import { EntityLink } from '@/components/detail/EntityLink';
import { HistoryTable } from '@/components/detail/HistoryTable';
import { formatCurrency } from '@/utils/formatters';
import type { StorageLocation } from '@/types/api';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'supplies', label: 'Supplies Here' },
  { id: 'children', label: 'Child Locations' },
];

function findPath(nodes: StorageLocation[], targetId: string, trail: StorageLocation[] = []): StorageLocation[] | null {
  for (const node of nodes) {
    const next = [...trail, node];
    if (node.id === targetId) return next;
    if (node.children?.length) {
      const found = findPath(node.children, targetId, next);
      if (found) return found;
    }
  }
  return null;
}

export function StorageLocationDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const { data: location, isLoading, isError } = useQuery({
    queryKey: ['storage-location', id],
    queryFn: () => storageLocationsApi.getById(id!),
    enabled: !!id,
  });

  const { data: tree } = useQuery({
    queryKey: ['storage-locations-tree'],
    queryFn: storageLocationsApi.getTree,
    enabled: !!id,
  });

  const { data: supplies, isLoading: suppliesLoading } = useQuery({
    queryKey: ['supplies-storage', id],
    queryFn: () => suppliesApi.getByStorageLocation(id!),
    enabled: !!id && activeTab === 'supplies',
  });

  const breadcrumb = useMemo(() => (tree && id ? findPath(tree, id) : null), [tree, id]);

  const setTab = (tabId: string) => setSearchParams({ tab: tabId });

  return (
    <DetailPageLayout
      backTo="/storage-locations"
      backLabel="Storage"
      title={location?.name ?? 'Storage Location'}
      subtitle={location?.locationType?.replace(/_/g, ' ')}
      isLoading={isLoading}
      notFound={isError || (!isLoading && !location)}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setTab}
    >
      {location && breadcrumb && breadcrumb.length > 1 && (
        <nav className="flex flex-wrap items-center gap-1 text-sm text-gray-600 dark:text-warm-sand -mt-2 mb-4">
          {breadcrumb.map((node, i) => (
            <span key={node.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3" />}
              {i === breadcrumb.length - 1 ? (
                <span className="text-gray-900 dark:text-warm-cream">{node.name}</span>
              ) : (
                <Link to={`/storage-locations/${node.id}`} className="hover:text-amber-450">
                  {node.name}
                </Link>
              )}
            </span>
          ))}
        </nav>
      )}

      {location && activeTab === 'overview' && (
        <div className="card p-5 space-y-3 max-w-xl">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div><dt className="text-gray-500">Type</dt><dd>{location.locationType}</dd></div>
            <div><dt className="text-gray-500">Sort order</dt><dd>{location.sortOrder}</dd></div>
          </dl>
          {location.description && (
            <p className="text-sm text-gray-600 dark:text-warm-sand pt-2 border-t border-gray-200 dark:border-charcoal-700">{location.description}</p>
          )}
        </div>
      )}

      {location && activeTab === 'supplies' && (
        <HistoryTable
          rows={supplies ?? []}
          isLoading={suppliesLoading}
          rowKey={(s) => s.id}
          emptyMessage="No supplies stored at this location."
          columns={[
            { key: 'name', header: 'Supply', render: (s) => <EntityLink type="supply" id={s.id} label={s.name} /> },
            { key: 'type', header: 'Type', render: (s) => s.supplyTypeDisplay },
            { key: 'qty', header: 'In stock', align: 'right', render: (s) => `${s.quantityInStock} ${s.unitOfMeasure}` },
            { key: 'value', header: 'Value', align: 'right', render: (s) => formatCurrency(s.stockValue) },
          ]}
        />
      )}

      {location && activeTab === 'children' && (
        <div className="space-y-2">
          {!location.children?.length ? (
            <p className="text-gray-600 dark:text-warm-sand py-8 text-center">No child locations.</p>
          ) : (
            location.children.map((child) => (
              <Link
                key={child.id}
                to={`/storage-locations/${child.id}`}
                className="card p-4 block hover:border-amber-450/30 transition-colors"
              >
                <p className="font-medium text-gray-900 dark:text-warm-cream">{child.name}</p>
                <p className="text-sm text-gray-500">{child.locationType}</p>
              </Link>
            ))
          )}
        </div>
      )}
    </DetailPageLayout>
  );
}
